import { useState, useEffect, useCallback } from "react";
import { useInstagram } from "../../contexts/InstagramContexts";

export default function Hero() {
  const { media, error } = useInstagram();
  const [slides, setSlides] = useState<any[]>([]);
  const [currentSlide, setCurrentSlide] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const customTestimonials = [
    {
      title: "PROFESSIONAL PHOTOSHOOT",
      quote: "Capture your best moments with flawless makeup",
    },
    {
      title: "GLAM TRANSFORMATION",
      quote: "From ordinary to extraordinary in minutes",
    },
    {
      title: "BRIDAL MAGIC",
      quote: "Your perfect wedding look starts here",
    },
    {
      title: "EDITORIAL STYLE",
      quote: "Red carpet looks for your special events",
    }
  ];

  useEffect(() => {
    if (media.length > 0) {
      // Create slides combining Instagram images with custom testimonials
      const createdSlides = media.slice(0, 4).map((post: any, index: any) => ({
        image: post.media_url || post.url,
        ...customTestimonials[index % customTestimonials.length]
      }));

      // Fill with defaults if not enough posts
      while (createdSlides.length < 4) {
        createdSlides.push({
          image: "/images/default-glam.jpg",
          ...customTestimonials[createdSlides.length % customTestimonials.length]
        });
      }

      // Create infinite loop effect by duplicating first and last items
      setSlides([
        createdSlides[createdSlides.length - 1],
        ...createdSlides,
        createdSlides[0],
      ]);
    }
  }, [media]);

  const handleSlideChange = useCallback(() => {
    if (slides.length <= 2) return;
    
    setIsTransitioning(true);
    setCurrentSlide((prev) => prev + 1);

    if (currentSlide >= slides.length - 2) {
      setTimeout(() => {
        setIsTransitioning(false);
        setCurrentSlide(1);
      }, 1000);
    } else {
      setTimeout(() => {
        setIsTransitioning(false);
      }, 1000);
    }
  }, [currentSlide, slides.length]);

  useEffect(() => {
    const timer = setInterval(handleSlideChange, 3000);
    return () => clearInterval(timer);
  }, [handleSlideChange]);

  const getSlideStyle = (index: number) => {
    const position = index - currentSlide;
    return {
      transform: `translateX(${position * 100}%)`,
      transition: isTransitioning ? "transform 1s ease-in-out" : "none",
      zIndex: position === 0 ? 1 : 0,
    };
  };

  const getTextStyle = (index: number) => {
    // Current visible slide is at currentSlide position
    const testimonialIndex = (currentSlide - 1) % customTestimonials.length;
    const position = index - testimonialIndex;
    return {
      transform: `translateY(${position * 100}%)`,
      transition: isTransitioning ? "transform 1s ease-in-out" : "none",
      display: position === 0 ? 'block' : 'none',
    };
  };

  if (error) {
    return (
      <section className="relative w-full h-[600px] lg:h-[590px] bg-gray-200 flex items-center justify-center">
        <div className="text-red-500">{error}</div>
      </section>
    );
  }

  return (
    <section className="relative lg:w-full h-[600px] lg:h-[690px] overflow-hidden mt-[4rem] lg:mt-[4.5rem] mx-auto">
      {/* Image Slider */}
      <div className="relative w-[full] h-full">
        {slides.map((slide, index) => (
          <div
            key={`img-${index}`}
            className="absolute top-0 left-0 w-full h-full mx-auto"
            style={getSlideStyle(index)}
          >
            <div
              className="w-full h-full bg-cover bg-no-repeat bg-center"
              style={{ backgroundImage: `url(${slide.image})` }}
            ></div>
          </div>
        ))}
      </div>

      <div className="absolute inset-0 bg-black opacity-50 z-10"></div>

      {/* Text Overlay - synchronized with images */}
      <div className="z-20 absolute inset-0 flex flex-col gap-4 lg:flex-row items-center justify-center lg:items-end lg:justify-end lg:pb-12 mt-16">
        <div className="relative rounded-lg w-[600px] px-auto h-[50px] lg:h-[80px] lg:h-[130px]">
          <div className="absolute h-[50px] lg:h-[80px] w-full overflow-hidden">
            {customTestimonials.map((testimonial, index) => (
              <div
                key={`text-${index}`}
                className="absolute w-full"
                style={getTextStyle(index)}
              >
                <h3
                  className="text-[18px] lg:text-[30px] text-white text-center lg:text-end lg:mr-40 font-bold italic"
                  style={{ fontFamily: "Cormorant Infant, serif" }}
                >
                  {testimonial.title}
                </h3>
                <p className="text-[12px] text-[18px] font-[500] text-white text-center lg:text-end lg:mr-40">
                  {testimonial.quote}
                </p>
              </div>
            ))}
          </div>
        </div>
        <div className="z-30 flex justify-center items-center mb-12 lg:hidden">
          <a
            href="/booking"
            className="bg-[#B87775] text-white text-[12px] lg:text-[12px] rounded-[8px] flex justify-center items-center hover:bg-[#9a7676] h-[48px] w-[175px]"
          >
            BOOK A SESSION
          </a>
        </div>
      </div>
    </section>
  );
}